import type { StaffingFilterStatus } from '@projektverwaltung/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NO_TAGS_VALUE } from '../components/v2/filters/filter-primitives.js';
import { resolveTagFilterStart } from '../pages/v2/phase-board-tag-filter.js';
import {
  type FilterableItem,
  type ProjectFilterOptions,
  type ProjectFilterState,
  buildProjectFilterOptions,
  filterProjectItems,
  hasActiveProjectFilters,
} from './project-filters.js';
import type { StaffingSortDirection, StaffingSortField } from './staffing-sort.js';
import type { StickyFilterValues } from './sticky-filters.js';
import { useStickyFilters } from './use-sticky-filters.js';

/**
 * Zustand + Gedächtnis der Staffing-Filterleiste (#114). Regeln: project-filters.ts.
 * Persistiert über useStickyFilters — die einzige Stelle, die Filter speichert.
 * Suche und Sortierung bleiben bewusst draußen (#63): eine gemerkte Suche sähe beim
 * nächsten Aufruf wie eine leere Liste aus.
 */
export type ProjectFilterView = 'staffing' | 'worklist_list';
export type StatusFilter = 'all' | StaffingFilterStatus;

export type UseProjectFiltersResult<T extends FilterableItem> = {
  state: ProjectFilterState;
  setSegment: (v: string) => void;
  togglePl: (id: string) => void;
  clearPl: () => void;
  setManager: (v: string | null) => void;
  toggleTag: (id: string) => void;
  clearTags: () => void;
  setSearch: (v: string) => void;
  setSortField: (f: StaffingSortField) => void;
  toggleSortDir: () => void;
  status: StatusFilter;
  setStatus: (s: StatusFilter) => void;
  withStatus: boolean;
  options: ProjectFilterOptions;
  filtered: T[];
  hasActive: boolean;
  reset: () => void;
};

const toggle = (id: string) => (prev: string[]) =>
  // Funktionales Update: bei zügigen Klicks würde ein Update auf Basis des gerenderten
  // Stands die vorherige Auswahl ersetzen statt ergänzen.
  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];

export function useProjectFilters<T extends FilterableItem>(opts: {
  view: ProjectFilterView;
  items: readonly T[];
  nameById: ReadonlyMap<string, string>;
  /**
   * Nur Staffing: Prädikat für den Status-Filter; ohne → kein Status-Filter im UI/Filtern.
   * Seit #158 der berechnete Positionsstatus (`status_anzeige`).
   */
  statusMatch?: (item: T, status: StaffingFilterStatus) => boolean;
}): UseProjectFiltersResult<T> {
  const { t } = useTranslation();
  const withStatus = opts.statusMatch !== undefined;
  const [segment, setSegment] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [pl, setPl] = useState<string[]>([]);
  const [manager, setManager] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  // #56: Default „Wieder fällig am" aufsteigend — das bisherige Verhalten.
  const [sortField, setSortField] = useState<StaffingSortField>('kl_followup_date');
  const [sortDir, setSortDir] = useState<StaffingSortDirection>('asc');

  // Rollback-Brücke (#81), gleiche Begründung wie im Phase Board.
  const tagRollback = tags.length === 1 && tags[0] !== NO_TAGS_VALUE ? tags[0] : null;
  // Snapshot je Sicht (#114): `worklist_list` kennt kein `status` im Schema
  // (WorklistListFiltersSchema). Zwei getrennte Objektliteral-Zweige statt eines
  // gemeinsamen mit optionalem `status`, damit TS jeden Zweig möglichst genau gegen
  // das passende Sicht-Schema prüft — verlässlich fängt das ein Leck trotzdem NICHT:
  // Zod-Objects sind hier nicht `strict`, ein zusätzliches `status`-Feld im
  // `worklist_list`-Zweig wäre strukturell weiter zuweisbar. Zur Laufzeit würde
  // `parseStored` (sticky-filters.ts) ein solches Leck beim nächsten Lesen still
  // HERAUSFILTERN statt den ganzen Snapshot zu verwerfen — Zod ignoriert unbekannte
  // Schlüssel eines Objekts standardmäßig, statt das gesamte Objekt abzulehnen. Der
  // eigentliche Riegel ist deshalb kein Typ, sondern der Laufzeittest in
  // kfml-worklist-filter.test.tsx (`not.toHaveProperty('status')`).
  const snapshot: StickyFilterValues<ProjectFilterView> =
    opts.view === 'staffing'
      ? { segment, status, pl, manager, tags, tag: tagRollback }
      : { segment, pl, manager, tags, tag: tagRollback };

  useStickyFilters(opts.view, {
    values: snapshot,
    apply: (f) => {
      if (f.segment !== undefined) setSegment(f.segment);
      if (withStatus && 'status' in f && f.status !== undefined) setStatus(f.status);
      if (f.pl !== undefined) setPl(f.pl ?? []);
      if (f.manager !== undefined) setManager(f.manager ?? null);
      const tagStart = resolveTagFilterStart(f);
      if (tagStart !== undefined) setTags(tagStart);
    },
  });

  const state: ProjectFilterState = { segment, pl, manager, tags, search, sortField, sortDir };
  const options = useMemo(
    () =>
      buildProjectFilterOptions(
        opts.items,
        opts.nameById,
        t('v2.staffing.filter_tag_none', 'Keine Tags'),
      ),
    [opts.items, opts.nameById, t],
  );
  const statusMatch = opts.statusMatch;
  // biome-ignore lint/correctness/useExhaustiveDependencies: state-Felder einzeln gelistet
  const filtered = useMemo(
    () =>
      filterProjectItems(
        opts.items,
        state,
        withStatus && status !== 'all' && statusMatch ? (i) => statusMatch(i, status) : undefined,
      ),
    [
      opts.items,
      segment,
      pl,
      manager,
      tags,
      search,
      sortField,
      sortDir,
      status,
      withStatus,
      statusMatch,
    ],
  );

  return {
    state,
    setSegment,
    togglePl: (id) => setPl(toggle(id)),
    clearPl: () => setPl([]),
    setManager,
    toggleTag: (id) => setTags(toggle(id)),
    clearTags: () => setTags([]),
    setSearch,
    setSortField,
    toggleSortDir: () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')),
    status,
    setStatus,
    withStatus,
    options,
    filtered,
    hasActive: hasActiveProjectFilters(state) || (withStatus && status !== 'all'),
    reset: () => {
      setSegment('');
      setStatus('all');
      setPl([]);
      setManager(null);
      setTags([]);
      setSearch('');
    },
  };
}
